import { useSelector } from "react-redux";
import { selectCurrentUserId } from "newui/features/Auth/authSelectors";
import { PanelEvaluationView } from "./panel/PanelEvaluationView";
import { PanelMemberScoringView } from "./panel/member/PanelMemberScoringView";
import type { Rfx } from "newui/features/Rfx/types/rfx.types";
import type { PanelMemberStatus, PanelMode } from "../../../types/panel.type";
import { EvaluationStepNumber, PanelMemberBlockedReason } from "../../../types/evaluation.type";
import { SoloEvaluationView } from "./components/SoloEvaluationView";

interface InitialEvaluationStepProps {
	currentStep: EvaluationStepNumber;
	disclaimerEnabled?: boolean;
	evalId?: number;
	isLoading?: boolean;
	panelMemberId?: number;
	panelMemberStatus?: string;
	panelMode?: PanelMode | string | null;
	rfxData: Rfx;
	scoringBlockedReason?: PanelMemberBlockedReason | null;
	onBeforeNext?: (check: () => Promise<boolean>) => void;
	onCompletionChange?: (isComplete: boolean) => void;
	onRefreshUserContext?: () => void;
}

export const InitialEvaluationStep: React.FC<InitialEvaluationStepProps> = ({
	currentStep,
	disclaimerEnabled = false,
	rfxData,
	evalId,
	panelMode,
	panelMemberId,
	panelMemberStatus,
	scoringBlockedReason,
	isLoading = false,
	onBeforeNext,
	onCompletionChange,
	onRefreshUserContext,
}) => {
	const rfxId = rfxData?.id;
	const currentUserId = useSelector(selectCurrentUserId);
	const isPanel = panelMode === "manual_panel" || panelMode === "ai_panel";
	const isRfxLead = currentUserId !== null && rfxData?.rfxLead?.id === currentUserId;

	// Panel mode: show panel-specific views
	if (isPanel && evalId) {
		if (isRfxLead) {
			return <PanelEvaluationView rfxData={rfxData} evalId={evalId} isRfxLead currentStep={currentStep} disclaimerEnabled={disclaimerEnabled} onCompletionChange={onCompletionChange} onRefreshUserContext={onRefreshUserContext} />;
		}

		if (panelMemberId && panelMemberStatus) {
			return (
				<PanelMemberScoringView
					currentStep={currentStep}
					rfxData={rfxData}
					evalId={evalId}
					memberId={panelMemberId}
					memberStatus={panelMemberStatus as PanelMemberStatus}
					scoringBlockedReason={scoringBlockedReason}
				/>
			);
		}
		// Non-lead, non-panel user in panel mode — show read-only via PanelEvaluationView
		return <PanelEvaluationView rfxData={rfxData} evalId={evalId} isRfxLead={false} />;
	}

	return (
		<SoloEvaluationView
			rfxId={rfxId}
			evalId={evalId}
			isLoading={isLoading}
			onBeforeNext={onBeforeNext}
			onCompletionChange={onCompletionChange}
		/>
	);
};
